/* eslint-disable @typescript-eslint/no-explicit-any */
import { SmartusComponentsModule } from '@affinis/smartus-components';
import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { provideMomentDateAdapter } from '@angular/material-moment-adapter';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  BaseSelectViewModel,
  BuildingRoomViewModel,
  CheckWorkspaceAvailabilityRequest,
  CostCenterClient,
  DeleteCostCenterUsageRequest,
  GetAvailableRoomAreaRequest,
  LocationClient,
  UpdateCostCenterUsageRequest,
} from '@core/api';
import { CostcenterSearchFormlyFieldService } from '@core/services/costcenter-search-formly-field-service/costcenter-search-formly-field.service';
import { Billing } from '@core/services/edit-room/billing';
import { UserService } from '@core/services/oauth-service/user.service';
import { OverrideModule } from '@mandants/telekom/override/override.module';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UserRoles } from '@shared/constants/user-roles.constants';
import { SafeHtmlPipe } from '@shared/pipes';
import { ToastrService } from 'ngx-toastr';
import {
  catchError,
  filter,
  forkJoin,
  map,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';

interface ICostCenterUsageForm {
  room: string;
  costcenter: any;
  usageFrom: Date;
  usageTo: Date;
  usageArea: number;
  note: string;
  recentChange: string;
  changeReason: string;
}

export const DATE_FORMAT = {
  parse: {
    dateInput: 'DD.MM.yyyy',
  },
  display: {
    dateInput: 'DD.MM.yyyy',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'app-edit-costcenter-usage',
  standalone: true,
  imports: [
    CommonModule,
    SmartusComponentsModule,
    TranslateModule,
    FormlyModule,
    FormsModule,
    ReactiveFormsModule,
    OverrideModule,
    MatTooltipModule,
    MatDialogModule,
    SafeHtmlPipe,
  ],
  templateUrl: './edit-costcenterusage.component.html',
  providers: [provideMomentDateAdapter(DATE_FORMAT)],
  styleUrls: ['./edit-costcenterusage.component.scss'],
})
export class EditCostcenterusageComponent implements OnInit {
  form = new FormGroup({});

  formFields: Array<FormlyFieldConfig>;

  changeReasons: BaseSelectViewModel[] = [];

  formModel = {} as ICostCenterUsageForm;

  isLoading = false;

  selectedCostcenter: string;

  canSave = true;

  canDelete = false;

  minDate = new Date();

  hintDescription: string = null;

  constructor(
    private translateService: TranslateService,
    private costCenterClient: CostCenterClient,
    private locationClient: LocationClient,
    public dialogRef: MatDialogRef<EditCostcenterusageComponent>,
    private costcenterFormlyFieldService: CostcenterSearchFormlyFieldService,
    private readonly userService: UserService,
    private readonly toastr: ToastrService,
    private readonly cd: ChangeDetectorRef,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      title: string;
      room: string;
      costcenter: string;
      start: Date;
      end: Date;
      note: string;
      recentChange: string;
      changeReason: BaseSelectViewModel;
      freeAreaInSquareMeters?: number;
      reservedAreaInSquareMeters?: number;
      isRoom: boolean;
      isDispo: boolean;
      id: string;
      archObjId: string;
      frozenStatus: boolean;
      description: string;
    }
  ) {}

  ngOnInit() {
    forkJoin({
      changeReasons: this.locationClient.getChangeReasons(),
      calculationTypes: this.locationClient
        .getCalculationTypes()
        .pipe(catchError(() => of([] as BaseSelectViewModel[]))),
      room: this.getRoomInformation(),
    }).subscribe({
      next: results => {
        this.changeReasons = results.changeReasons;
        this.setCalculationTypeHint(results.room, results.calculationTypes);
        this.initializeFormFields();
      },
    });

    const today = new Date();
    this.minDate = new Date();

    const loggedUserRoles = this.userService.getCurrentUserRoles();

    const hasFlmRole = loggedUserRoles.includes(
      UserRoles.SMARTPORTAL_REM_FLM_DATENPFLEGE
    );

    const hasGlobalRole = loggedUserRoles.includes(
      UserRoles.SMARTPORTAL_REM_DATENPFLEGE
    );

    if (hasFlmRole) {
      this.minDate.setDate(this.minDate.getDate() - 1);
    } else if (!hasGlobalRole) {
      this.minDate.setDate(this.minDate.getDate() + 1);
    }
    const startDate = new Date(this.data.start);
    if (startDate > today && !this.data.isDispo) {
      this.canDelete = true;
    }

    this.canSave = !this.data.frozenStatus;
  }

  private getRoomInformation(): Observable<BuildingRoomViewModel> {
    const roomSpaceId = this.data.archObjId?.split('/').slice(0, 3).join('/');

    if (!roomSpaceId) {
      return of(null);
    }

    return this.locationClient
      .getBuildingMaintenanceRoomInformation(roomSpaceId)
      .pipe(catchError(() => of(null)));
  }

  private setCalculationTypeHint(
    room: BuildingRoomViewModel,
    calculationTypes: BaseSelectViewModel[]
  ) {
    const calculationType = room?.calculationType;

    const matchesEditedLevel = this.data.isRoom
      ? calculationType === Billing.roomCostCenter
      : calculationType === Billing.workPlace;

    if (!calculationType || matchesEditedLevel) {
      return;
    }

    const calculationTypeName = calculationTypes.find(
      type => type.id === calculationType
    )?.name;

    if (!calculationTypeName) {
      return;
    }

    this.hintDescription = this.translateService.instant(
      this.data.isRoom
        ? 'costcenter.costcenter_usage.edit_dialog.hint.room'
        : 'costcenter.costcenter_usage.edit_dialog.hint.working_space',
      { calculationType: calculationTypeName }
    );
  }

  initializeFormFields() {
    this.formFields = [
      {
        wrappers: ['readOnly', 'form-field'],
        type: 'input',
        key: 'room',
        defaultValue: this.data.room,
        props: {
          readonly: true,
          label: this.data.isRoom
            ? this.translateService.instant(
                'costcenter.costcenter_usage.edit_dialog.room'
              )
            : this.translateService.instant(
                'costcenter.costcenter_usage.working_space'
              ),
        },
      },
      {
        key: 'costcenter',
        fieldGroup: [
          ...this.costcenterFormlyFieldService.autocompleteSearch(
            this.translateService.instant(
              'costcenter.costcenter_usage.edit_dialog.costcenter'
            ),
            true,
            null,
            this.data.costcenter,
            id => this.onSelectCostcenter(id),
            () => this.onDeleteCostcenter(),
            true,
            null,
            this.data.frozenStatus
          ),
        ],
      },
      {
        type: 'chapter',
        props: {
          chapterTitle: this.translateService.instant(
            'costcenter.costcenter_usage.edit_dialog.usage_period'
          ),
        },
      },
      {
        type: 'flex-layout-col',
        fieldGroup: [
          {
            type: 'datepicker',
            key: 'usageFrom',
            defaultValue: this.data?.start,
            props: {
              label: this.translateService.instant(
                'costcenter.costcenter_usage.edit_dialog.usage_from'
              ),
              datepickerOptions: {
                min: this.minDate,
              },
              required: true,
              disabled: this.data.frozenStatus,
            },
            validators: {
              dateValidation: {
                expression: c => {
                  const usageToControl = c.parent.get('usageTo');

                  if (
                    usageToControl &&
                    usageToControl.valid === false &&
                    usageToControl.value > c.value
                  ) {
                    // Trigger validation on usageFrom
                    usageToControl.updateValueAndValidity();
                  }
                  if (usageToControl.value && c.value > usageToControl.value) {
                    return false;
                  }
                  if (this.canSave === false) {
                    return false;
                  }
                  return true;
                },
                message: () => {
                  if (this.canSave === false) {
                    return this.translateService.instant(
                      'costcenter.costcenter_usage.edit_dialog.error.no_free_area'
                    );
                  } else {
                    return this.translateService.instant(
                      'costcenter.costcenter_usage.edit_dialog.error.start_before_end_date'
                    );
                  }
                },
              },
            },
            validation: {
              messages: {
                min: this.translateService.instant(
                  'costcenter.costcenter_usage.edit_dialog.error.invalid_date'
                ),
              },
            },
            hooks: {
              onInit: field => {
                return field.options.fieldChanges.pipe(
                  filter(e => {
                    return e.type === 'valueChanges' && e.field === field;
                  }),
                  map(() =>
                    this.data.isRoom
                      ? {
                          archObjId: this.data.archObjId,
                          usageId: this.data.id,
                          from: field.formControl.value,
                          until: this.form.value['usageTo'],
                        }
                      : {
                          id: this.data.id,
                          archObjId: this.data.archObjId,
                          validFromDate: field.formControl.value,
                          validUntilDate: this.form.value['usageTo'],
                        }
                  ),
                  switchMap(
                    (
                      req:
                        | GetAvailableRoomAreaRequest
                        | CheckWorkspaceAvailabilityRequest
                    ) =>
                      this.data.isRoom
                        ? this.costCenterClient
                            .getAvailableRoomArea(
                              req as GetAvailableRoomAreaRequest
                            )
                            .pipe(
                              catchError(() => {
                                return of(false);
                              })
                            )
                        : this.costCenterClient
                            .checkWorkspaceAvailability(
                              req as CheckWorkspaceAvailabilityRequest
                            )
                            .pipe(
                              catchError(() => {
                                return of(false);
                              })
                            )
                  ),
                  tap(response => {
                    this.canSave = this.data.isRoom ? response > 0 : response;
                    field.formControl.updateValueAndValidity({
                      emitEvent: false,
                    });
                  })
                );
              },
            },
          },
          {
            type: 'datepicker',
            key: 'usageTo',
            defaultValue:
              new Date(this.data.end.valueOf()) || new Date('01.01.2099'),
            props: {
              label: this.translateService.instant(
                'costcenter.costcenter_usage.edit_dialog.usage_to'
              ),
              required: true,
              disabled: this.data.frozenStatus,
            },
            validators: {
              dateValidation: {
                expression: c => {
                  const usageFromControl = c.parent.get('usageFrom');

                  if (
                    usageFromControl &&
                    usageFromControl.valid === false &&
                    usageFromControl.value < c.value
                  ) {
                    usageFromControl.updateValueAndValidity({
                      emitEvent: false,
                    });
                  }

                  if (
                    usageFromControl.value &&
                    c.value < usageFromControl.value
                  ) {
                    return false;
                  }

                  if (this.canSave === false) {
                    return false;
                  }

                  return true;
                },
                message: () => {
                  if (this.canSave === false) {
                    return this.translateService.instant(
                      'costcenter.costcenter_usage.edit_dialog.error.no_free_area'
                    );
                  } else {
                    return this.translateService.instant(
                      'costcenter.costcenter_usage.edit_dialog.error.start_before_end_date'
                    );
                  }
                },
              },
            },
            hooks: {
              onInit: field => {
                return field.options.fieldChanges.pipe(
                  filter(e => {
                    return e.type === 'valueChanges' && e.field === field;
                  }),
                  map(() =>
                    this.data.isRoom
                      ? {
                          archObjId: this.data.archObjId,
                          usageId: this.data.id,
                          from: this.form.value['usageFrom'],
                          until: field.formControl.value,
                        }
                      : {
                          id: this.data.id,
                          archObjId: this.data.archObjId,
                          validFromDate: this.form.value['usageFrom'],
                          validUntilDate: field.formControl.value,
                        }
                  ),
                  switchMap(
                    (
                      req:
                        | GetAvailableRoomAreaRequest
                        | CheckWorkspaceAvailabilityRequest
                    ) =>
                      this.data.isRoom
                        ? this.costCenterClient
                            .getAvailableRoomArea(
                              req as GetAvailableRoomAreaRequest
                            )
                            .pipe(
                              catchError(() => {
                                return of(false);
                              })
                            )
                        : this.costCenterClient
                            .checkWorkspaceAvailability(
                              req as CheckWorkspaceAvailabilityRequest
                            )
                            .pipe(
                              catchError(() => {
                                return of(false);
                              })
                            )
                  ),
                  tap(response => {
                    this.canSave = this.data.isRoom ? response > 0 : response;
                    field.formControl.updateValueAndValidity({
                      emitEvent: false,
                    });
                  })
                );
              },
            },
          },
          {
            type: 'button',
            key: 'today',
            className: 'form-button',
            props: {
              label: this.translateService.instant(
                'costcenter.costcenter_usage.edit_dialog.today'
              ),
              onClick: () => {
                (this.form.get('usageTo') as any).setValue(new Date());
                this.deleteValidationErrorForUsageFrom();
              },
            },
          },
        ],
      },
      {
        type: 'chapter',
        hide: !this.data.isRoom,
        props: {
          chapterTitle: this.translateService.instant(
            'costcenter.costcenter_usage.edit_dialog.usage_share'
          ),
        },
      },
      {
        type: 'input-with-text',
        key: 'usageArea',
        hide: !this.data.isRoom,
        defaultValue: this.data.reservedAreaInSquareMeters?.toString(),
        props: {
          label: this.translateService.instant(
            'costcenter.costcenter_usage.edit_dialog.usage_area'
          ),
          placeholder: this.data.freeAreaInSquareMeters?.toString(),
          required: true,
          text: `von ${this.data.freeAreaInSquareMeters.toString()} m²`,
          type: 'number',
          disabled: this.data.frozenStatus,
        },
        validators: {
          usageAreaValidation: {
            expression: c => {
              if (
                parseFloat(c.value?.toString().replace(',', '.')) >
                this.data.freeAreaInSquareMeters
              ) {
                return false;
              }
              return true;
            },
            message: () => {
              return this.translateService.instant(
                'costcenter.costcenter_usage.edit_dialog.error.area_too_high'
              );
            },
          },
        },
      },
      {
        type: 'input',
        key: 'note',
        defaultValue: this.data.note,
        props: {
          label: this.translateService.instant(
            'costcenter.costcenter_usage.edit_dialog.note'
          ),
          disabled: this.data.frozenStatus,
        },
      },
      {
        type: 'input',
        key: 'recentChange',
        wrappers: ['readOnly'],
        defaultValue: this.data.recentChange,
        props: {
          label: this.translateService.instant(
            'costcenter.costcenter_usage.edit_dialog.recent_change'
          ),
        },
      },
      {
        type: 'input-search',
        key: 'changeReason',
        props: {
          label: this.translateService.instant(
            'costcenter.costcenter_usage.edit_dialog.change_reason'
          ),
          options: this.changeReasons.map((reason: BaseSelectViewModel) => ({
            value: reason.id,
            label: reason.name,
          })),
          required: true,
          disabled: this.data.frozenStatus,
        },
      },
    ];
  }

  onSelectCostcenter(id: string) {
    this.selectedCostcenter = id;
  }

  onDeleteCostcenter() {
    this.selectedCostcenter = null;
  }

  onSave() {
    this.deleteValidationErrorForUsageFrom();

    if (this.form.valid) {
      this.isLoading = true;
      const formValue = this.form.value as ICostCenterUsageForm;
      const request: UpdateCostCenterUsageRequest = {
        id: this.data.id,
        costCenterId: formValue.costcenter.searchResult.costCenter,
        usageFrom: formValue.usageFrom,
        usageUntil: formValue.usageTo,
        area: formValue.usageArea,
        changeReason: formValue.changeReason,
        remarks: formValue.note,
      };
      if (!this.data.isRoom && this.data.isDispo) {
        this.costCenterClient.createWorkspaceUsage(request).subscribe({
          next: () => {
            this.dialogRef.close(true);
            this.toastr.success(
              this.translateService.instant(
                'costcenter.costcenter_usage.edit_dialog.success.create'
              )
            );
          },
          error: () => {
            this.toastr.error(
              this.translateService.instant('generic.errorMessage')
            );
            this.isLoading = false;
          },
        });
      } else if (!this.data.isRoom && !this.data.isDispo) {
        request.costCenterId =
          formValue.costcenter.searchResult.costCenter ||
          formValue.costcenter.searchResult.id;
        this.costCenterClient.updateWorkspaceUsage(request).subscribe({
          next: () => {
            this.dialogRef.close(true);
            this.toastr.success(
              this.translateService.instant(
                'costcenter.costcenter_usage.edit_dialog.success.update'
              )
            );
          },
          error: () => {
            this.toastr.error(
              this.translateService.instant('generic.errorMessage')
            );
            this.isLoading = false;
          },
        });
      } else if (this.data.isRoom && this.data.isDispo) {
        this.costCenterClient.createRoomUsage(request).subscribe({
          next: () => {
            this.dialogRef.close(true);
            this.toastr.success(
              this.translateService.instant(
                'costcenter.costcenter_usage.edit_dialog.success.create'
              )
            );
          },
          error: () => {
            this.toastr.error(
              this.translateService.instant('generic.errorMessage')
            );
            this.isLoading = false;
          },
        });
      } else if (this.data.isRoom && !this.data.isDispo) {
        this.costCenterClient.updateRoomUsage(request).subscribe({
          next: () => {
            this.dialogRef.close(true);
            this.toastr.success(
              this.translateService.instant(
                'costcenter.costcenter_usage.edit_dialog.success.update'
              )
            );
          },
          error: () => {
            this.toastr.error(
              this.translateService.instant('generic.errorMessage')
            );
            this.isLoading = false;
          },
        });
      }
    } else {
      this.form.markAllAsTouched();
    }
  }

  onDelete() {
    const formValue = this.form.value as ICostCenterUsageForm;

    if (!formValue.changeReason || formValue.changeReason === '') {
      this.toastr.error(
        this.translateService.instant(
          'costcenter.costcenter_usage.edit_dialog.error.missing_change_reason'
        )
      );
      return;
    }

    this.isLoading = true;
    const req: DeleteCostCenterUsageRequest = {
      id: this.data.id,
      changeReason: formValue.changeReason,
    };

    if (this.data.isRoom) {
      this.costCenterClient.deleteRoomUsage(req).subscribe({
        next: () => {
          this.dialogRef.close(true);
          this.toastr.success(
            this.translateService.instant(
              'costcenter.costcenter_usage.edit_dialog.success.delete'
            )
          );
        },
        error: () => {
          this.toastr.error(
            this.translateService.instant('generic.errorMessage')
          );
          this.isLoading = false;
        },
      });
    } else {
      this.costCenterClient.deleteWorkspaceUsage(req).subscribe({
        next: () => {
          this.dialogRef.close(true);
          this.toastr.success(
            this.translateService.instant(
              'costcenter.costcenter_usage.edit_dialog.success.delete'
            )
          );
        },
        error: () => {
          this.toastr.error(
            this.translateService.instant('generic.errorMessage')
          );
          this.isLoading = false;
        },
      });
    }
  }

  deleteValidationErrorForUsageFrom() {
    const control = this.form.get('usageFrom');

    if (control && control.hasError('matDatepickerMin') && !control.dirty) {
      const currentErrors = { ...control.errors };
      delete currentErrors['matDatepickerMin'];

      control.setErrors(
        Object.keys(currentErrors).length > 0 ? currentErrors : null
      );
    }
  }
}
